/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// Extracted from original/src/components/InstallmentsView.tsx:3427-3525 — the A4 print of the filtered contracts list
// ("تقرير كشف عقود الأقساط والبيع بالتقسيط", the `contracts-list-print-area` element, printed / saved as PDF by
// `handlePrintContractsList` / `handlePDFContractsList` :189-195). ADR-08 §2.1 "copy, then cut logic".
// Kept: the header row (initial tile, office name and line, title / date / count block), the three-box filter strip
// (status, start date, end date with "غير محدد"), the eight-column table with its status words and colours, the two
// signature blocks, every class string.
// Cut: the hard-coded "شركة الفيلسوف للصيرفة" and its line (R-14, F-337) → the office identity (`office`, D-052, D-125;
// the tile shows the office's first letter, as p06); `new Date().toLocaleDateString('ar-IQ')` (browser zone) →
// `dateLabel` in the office time zone (D-064, D-162); `Math.max(0, total_price - total_paid)` and `formatNumber` on
// JavaScript numbers (ADR-02 §2.9) → preformatted labels from the server's read model.
// Changed by decision: "المدفوع" includes the down payment and "المتبقي بالأقساط" is the server's true remaining after
// the down payment and the active payments (D-109 (6), D-108, as p06); a cancelled contract shows its own status word
// (D-312: contracts are cancelled in the Journal; old states were active / completed only). All strings are
// `print.inlist.*` keys.
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { PrintOffice } from './StatementA4.js';

export interface InstallmentListA4Row {
  readonly id: string;
  readonly contractNumber: string;
  readonly customerName: string;
  readonly itemName: string;
  readonly totalLabel: string;
  readonly paidLabel: string;
  readonly remainingLabel: string;
  readonly status: 'active' | 'completed' | 'cancelled';
}

export interface InstallmentListA4Props {
  readonly locale: Locale;
  readonly id?: string;
  readonly office: PrintOffice;
  readonly dateLabel: string;
  /** The status filter word as shown on the screen ("الكل" / "جارية" / "مكتملة"). */
  readonly filterLabel: string;
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly rows: readonly InstallmentListA4Row[];
}

export function InstallmentListA4(p: InstallmentListA4Props) {
  const t = createT(p.locale);
  const initial = (p.office.name.trim()[0] ?? 'ف').toUpperCase();
  return (
    <div id={p.id ?? 'contracts-list-print-area'} className="font-sans p-8 bg-white text-black printable-report" style={{ direction: p.locale === 'ar' ? 'rtl' : 'ltr' }} data-testid="installment-list-a4">
      {/* Header */}
      <div className="flex items-center justify-between border-b-2 border-gray-900 pb-4 mb-6">
        <div className="flex items-center gap-3">
          {p.office.logoUrl ? (
            <img src={p.office.logoUrl} alt="" className="w-12 h-12 rounded-xl object-contain" />
          ) : (
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-black text-xl">{initial}</div>
          )}
          <div>
            <h1 className="text-xl font-black text-gray-900">{p.office.name}</h1>
            <p className="text-xs text-gray-600">{[p.office.address, p.office.phone1, p.office.phone2].filter(Boolean).join(' — ') || t('print.inlist.office_line')}</p>
          </div>
        </div>
        <div className="text-left">
          <h2 className="text-base font-black text-gray-900">{t('print.inlist.title')}</h2>
          <p className="text-xs text-gray-600">{t('print.inlist.date', { d: p.dateLabel })}</p>
          <p className="text-xs text-gray-600">{t('print.inlist.count', { n: p.rows.length })}</p>
        </div>
      </div>

      {/* Filters and Metadata Summary */}
      <div className="grid grid-cols-3 gap-4 bg-gray-50 p-3 rounded-xl mb-6 border border-gray-200 text-xs font-bold">
        <div>
          <span className="text-gray-500 block">{t('print.inlist.filter_status')}</span>
          <span className="text-gray-900">{p.filterLabel}</span>
        </div>
        <div>
          <span className="text-gray-500 block">{t('print.inlist.filter_from')}</span>
          <span className="text-gray-900">{p.fromDate || t('print.inlist.unset')}</span>
        </div>
        <div>
          <span className="text-gray-500 block">{t('print.inlist.filter_to')}</span>
          <span className="text-gray-900">{p.toDate || t('print.inlist.unset')}</span>
        </div>
      </div>

      {/* Table of Contracts */}
      <table className="w-full border-collapse border border-gray-300 text-right text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2 font-black text-gray-900 text-center">#</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900">{t('print.inlist.col_number')}</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900">{t('print.inlist.col_customer')}</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900">{t('print.inlist.col_item')}</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900 text-left">{t('print.inlist.col_total')}</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900 text-left">{t('print.inlist.col_paid')}</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900 text-left">{t('print.inlist.col_remaining')}</th>
            <th className="border border-gray-300 p-2 font-black text-gray-900 text-center">{t('print.inlist.col_status')}</th>
          </tr>
        </thead>
        <tbody>
          {p.rows.map((c, idx) => (
            <tr key={c.id}>
              <td className="border border-gray-300 p-2 text-center font-bold">{idx + 1}</td>
              <td className="border border-gray-300 p-2 font-mono font-bold">{c.contractNumber}</td>
              <td className="border border-gray-300 p-2 font-black text-gray-900">{c.customerName}</td>
              <td className="border border-gray-300 p-2">{c.itemName}</td>
              <td className="border border-gray-300 p-2 font-mono font-bold text-left">{c.totalLabel}</td>
              <td className="border border-gray-300 p-2 font-mono font-bold text-left">{c.paidLabel}</td>
              <td className="border border-gray-300 p-2 font-mono font-bold text-left text-amber-700">{c.remainingLabel}</td>
              <td className="border border-gray-300 p-2 text-center font-bold">
                {c.status === 'active' ? (
                  <span className="text-emerald-700">{t('print.inlist.st_active')}</span>
                ) : c.status === 'completed' ? (
                  <span className="text-purple-700">{t('print.inlist.st_completed')}</span>
                ) : (
                  <span className="text-rose-700">{t('print.inlist.st_cancelled')}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer / Signatures */}
      <div className="grid grid-cols-2 gap-8 pt-12 border-t border-dashed border-gray-400 mt-12 text-center">
        <div className="space-y-16">
          <span className="text-xs text-gray-500 block font-bold">{t('print.inlist.sign_branch')}</span>
          <div className="w-32 h-0.5 bg-gray-400 mx-auto"></div>
        </div>
        <div className="space-y-16">
          <span className="text-xs text-gray-500 block font-bold">{t('print.inlist.sign_auditor')}</span>
          <div className="w-32 h-0.5 bg-gray-400 mx-auto"></div>
        </div>
      </div>
    </div>
  );
}
